import { AlertTriangle, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { OptionPicker, type PickerOption } from "@/components/cinch/OptionPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  useCreateChurn,
  useCreateHeadcount,
  useCreateOneOff,
  useCreateVendor,
  useDeleteChurn,
  useDeleteHeadcount,
  useDeleteOneOff,
  useDeleteVendor,
  useUpdateChurn,
  useUpdateHeadcount,
  useUpdateOneOff,
  useUpdateVendor,
  type Churn,
  type Headcount,
  type OneOff,
  type Vendor,
} from "../api";
import { KIND_LABEL } from "../lib";

/** What the drawer is editing. `null` for a fresh row. */
export type DrawerTarget =
  | { kind: "headcount"; row: Headcount | null; date?: string }
  | { kind: "churn"; row: Churn | null; date?: string }
  | { kind: "one_off"; row: OneOff | null; date?: string }
  | { kind: "vendor"; row: Vendor | null; date?: string };

const HEADCOUNT_STATES: PickerOption[] = [
  { value: "speculative", label: "Speculative", description: "We might hire this. A plan, not a person." },
  { value: "offer_accepted", label: "Offer accepted", description: "Offer out and accepted; not yet on payroll." },
  { value: "started", label: "Started", description: "They're here and working; the pay calendar hasn't caught up." },
  { value: "baked_in", label: "Baked in", description: "Already inside the pay-run amounts. Adds NO extra cost." },
];

const CHURN_STATES: PickerOption[] = [
  { value: "speculative", label: "Speculative", description: "At risk. Does NOT reduce the cash forecast." },
  { value: "notice_given", label: "Notice given", description: "Notice received; the end date is known." },
  { value: "confirmed", label: "Confirmed", description: "The monthly close settled these dates." },
];

const CERTAINTIES: PickerOption[] = [
  { value: "speculative", label: "Speculative", description: "Might happen." },
  { value: "expected", label: "Expected", description: "Likely, not committed." },
  { value: "committed", label: "Committed", description: "Signed, invoiced, or legally due." },
];

/**
 * Create/edit any calendar assumption in one drawer.
 *
 * Quick view is the default because most edits are a date or an amount; the
 * expand chevron gives the full form when something needs explaining. Every
 * type here has the same shape — an effective date, a cash date, and how firm
 * it is — so the form reads the same way whichever one you opened.
 */
export function AssumptionDrawer({
  target,
  expanded,
  onExpandedChange,
  onClose,
  bucketNames,
}: {
  target: DrawerTarget | null;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
  bucketNames: string[];
}) {
  const kindLabel = target ? (KIND_LABEL[target.kind] ?? target.kind) : "";
  const isNew = target?.row == null;

  return (
    <EntityDrawer
      open={target != null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={isNew ? `New ${kindLabel.toLowerCase()} assumption` : kindLabel}
      subtitle={
        isNew
          ? "Both dates matter: when it happens, and when the money moves."
          : undefined
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-4 p-4">
        {target?.kind === "headcount" && (
          <HeadcountForm target={target} onClose={onClose} expanded={expanded} />
        )}
        {target?.kind === "churn" && (
          <ChurnForm target={target} onClose={onClose} expanded={expanded} />
        )}
        {target?.kind === "one_off" && (
          <OneOffForm
            target={target}
            onClose={onClose}
            expanded={expanded}
            bucketNames={bucketNames}
          />
        )}
        {target?.kind === "vendor" && (
          <VendorForm target={target} onClose={onClose} expanded={expanded} />
        )}
      </div>
    </EntityDrawer>
  );
}

// ── shared bits ─────────────────────────────────────────────────────────────

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Actions({
  isNew,
  pending,
  canSave,
  onSave,
  onDelete,
  onCancel,
}: {
  isNew: boolean;
  pending: boolean;
  canSave: boolean;
  onSave: () => void;
  onDelete?: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
      {!isNew && onDelete ? (
        <Button variant="ghost" size="sm" onClick={onDelete} disabled={pending}>
          <Trash2 className="mr-1.5 h-3.5 w-3.5 text-danger" /> Delete
        </Button>
      ) : (
        <span />
      )}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={pending || !canSave}>
          {pending ? "Saving…" : isNew ? "Create" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function useSaved(onClose: () => void, what: string) {
  return {
    onSuccess: () => {
      toast.success(`${what} saved`);
      onClose();
    },
    onError: (e: unknown) =>
      toast.error("Couldn't save", { description: (e as Error).message }),
  };
}

// ── headcount ───────────────────────────────────────────────────────────────

function HeadcountForm({
  target,
  onClose,
  expanded,
}: {
  target: Extract<DrawerTarget, { kind: "headcount" }>;
  onClose: () => void;
  expanded: boolean;
}) {
  const row = target.row;
  const create = useCreateHeadcount();
  const update = useUpdateHeadcount();
  const remove = useDeleteHeadcount();
  const handlers = useSaved(onClose, "Headcount plan");

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [perPaycheck, setPerPaycheck] = useState("");
  const [planState, setPlanState] = useState("speculative");
  const [role, setRole] = useState("");
  const [department, setDepartment] = useState("");
  const [salary, setSalary] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    setName(row?.name ?? "");
    setStartDate(row?.start_date ?? target.date ?? "");
    setPerPaycheck(row ? String(row.add_per_paycheck) : "");
    setPlanState(row?.plan_state ?? "speculative");
    setRole(row?.role ?? "");
    setDepartment(row?.department ?? "");
    setSalary(row?.annual_salary != null ? String(row.annual_salary) : "");
    setNote(row?.note ?? "");
  }, [row, target.date]);

  const body = {
    name,
    start_date: startDate,
    add_per_paycheck: Number(perPaycheck || 0),
    plan_state: planState,
    role: role || null,
    department: department || null,
    annual_salary: salary ? Number(salary) : null,
    note: note || null,
  };
  const pending = create.isPending || update.isPending || remove.isPending;

  return (
    <>
      <div className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2 text-[11px]">
        <strong>The start date is an accrual date, not a cash date.</strong> Cost
        begins accruing then and lands in the next pay run — the calendar draws
        both.
      </div>

      <Row label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Row>
      <Row
        label="Start date (accrual)"
        hint="For a departure, their last day."
      >
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </Row>
      <Row
        label="Cost per paycheck"
        hint="Negative for a hire (cost added), positive for a departure (cost removed). Loaded cost, not salary ÷ 24."
      >
        <Input
          type="number"
          value={perPaycheck}
          onChange={(e) => setPerPaycheck(e.target.value)}
          placeholder="-10000"
        />
      </Row>
      <Row label="Planning state">
        <OptionPicker
          label="Planning state"
          value={planState}
          options={HEADCOUNT_STATES}
          onChange={setPlanState}
        />
      </Row>
      {planState === "baked_in" && (
        <div className="flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning-light/40 px-3 py-2 text-[11px] text-warning">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
          <span>
            Baked in means the pay-run amounts <em>already include</em> this
            person, so the forecast adds nothing further. Marking someone baked
            in when they aren't understates payroll; the reverse double-counts
            them.
          </span>
        </div>
      )}

      {expanded && (
        <>
          <Row label="Role">
            <Input value={role} onChange={(e) => setRole(e.target.value)} />
          </Row>
          <Row label="Department">
            <Input
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            />
          </Row>
          <Row label="Annual salary" hint="Informational — the model uses cost per paycheck.">
            <Input
              type="number"
              value={salary}
              onChange={(e) => setSalary(e.target.value)}
            />
          </Row>
          <Row label="Notes">
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </Row>
        </>
      )}

      <Actions
        isNew={row == null}
        pending={pending}
        canSave={!!name && !!startDate}
        onCancel={onClose}
        onSave={() =>
          row
            ? update.mutate({ id: row.id, body }, handlers)
            : create.mutate(body, handlers)
        }
        onDelete={
          row
            ? () =>
                remove.mutate(row.id, {
                  onSuccess: () => {
                    toast.success("Headcount plan removed");
                    onClose();
                  },
                })
            : undefined
        }
      />
    </>
  );
}

// ── churn ───────────────────────────────────────────────────────────────────

function ChurnForm({
  target,
  onClose,
  expanded,
}: {
  target: Extract<DrawerTarget, { kind: "churn" }>;
  onClose: () => void;
  expanded: boolean;
}) {
  const row = target.row;
  const create = useCreateChurn();
  const update = useUpdateChurn();
  const remove = useDeleteChurn();
  const handlers = useSaved(onClose, "Churn assumption");

  const [account, setAccount] = useState("");
  const [state, setState] = useState("speculative");
  const [noticeDate, setNoticeDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [mrr, setMrr] = useState("");
  const [terms, setTerms] = useState("0");
  const [qbId, setQbId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    setAccount(row?.account_name ?? "");
    setState(row?.state ?? "speculative");
    setNoticeDate(row?.notice_date ?? "");
    setEndDate(row?.end_date ?? target.date ?? "");
    setMrr(row ? String(row.mrr) : "");
    setTerms(row ? String(row.terms_days) : "0");
    setQbId(row?.qb_id != null ? String(row.qb_id) : "");
    setReason(row?.reason ?? "");
  }, [row, target.date]);

  const body = {
    account_name: account,
    state,
    notice_date: noticeDate || null,
    end_date: endDate,
    mrr: Number(mrr || 0),
    terms_days: Number(terms || 0),
    qb_id: qbId ? Number(qbId) : null,
    reason: reason || null,
  };
  const pending = create.isPending || update.isPending || remove.isPending;
  const locked = row?.confirmed_by_recon ?? false;

  const noticeGap =
    noticeDate && endDate
      ? Math.round(
          (new Date(endDate).getTime() - new Date(noticeDate).getTime()) / 86_400_000,
        )
      : null;

  return (
    <>
      {locked && (
        <div className="rounded-md border border-success/40 bg-success-light/40 px-3 py-2 text-[11px] text-success">
          The monthly close settled these dates (reconciliation step 6). Editing
          here will be overwritten the next time the close syncs — change it in
          the close instead.
        </div>
      )}

      <Row label="Account">
        <Input value={account} onChange={(e) => setAccount(e.target.value)} />
      </Row>
      <Row label="Confidence">
        <OptionPicker
          label="Churn confidence"
          value={state}
          options={CHURN_STATES}
          onChange={setState}
        />
      </Row>
      {state === "speculative" && (
        <div className="flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning-light/40 px-3 py-2 text-[11px] text-warning">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
          <span>
            Speculative churn shows on the calendar but does <em>not</em> reduce
            the cash forecast — quietly shrinking a forecast on a hunch is how a
            model stops being trusted. Move it to “notice given” once notice
            actually arrives.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Row label="Notice date" hint="When they told us.">
          <Input
            type="date"
            value={noticeDate}
            onChange={(e) => setNoticeDate(e.target.value)}
          />
        </Row>
        <Row label="End date" hint="Last day we bill.">
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Row>
      </div>
      {noticeGap != null && noticeGap >= 0 && (
        <p className="text-[11px] text-muted-foreground">
          {noticeGap} days of warning. The median across our history is about 60
          — comfortably inside a 13-week forecast.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Row label="MRR lost">
          <Input type="number" value={mrr} onChange={(e) => setMrr(e.target.value)} />
        </Row>
        <Row label="Payment terms (days)" hint="Cash stops this long after the end date.">
          <Input
            type="number"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
          />
        </Row>
      </div>

      {expanded && (
        <>
          <Row label="QuickBooks customer id" hint="Links this to the monthly close's churn record.">
            <Input value={qbId} onChange={(e) => setQbId(e.target.value)} />
          </Row>
          <Row label="Reason">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Row>
        </>
      )}

      <Actions
        isNew={row == null}
        pending={pending}
        canSave={!!account && !!endDate}
        onCancel={onClose}
        onSave={() =>
          row
            ? update.mutate({ id: row.id, body }, handlers)
            : create.mutate(body, handlers)
        }
        onDelete={
          row
            ? () =>
                remove.mutate(row.id, {
                  onSuccess: () => {
                    toast.success("Churn assumption removed");
                    onClose();
                  },
                })
            : undefined
        }
      />
    </>
  );
}

// ── one-off ─────────────────────────────────────────────────────────────────

function OneOffForm({
  target,
  onClose,
  expanded,
  bucketNames,
}: {
  target: Extract<DrawerTarget, { kind: "one_off" }>;
  onClose: () => void;
  expanded: boolean;
  bucketNames: string[];
}) {
  const row = target.row;
  const create = useCreateOneOff();
  const update = useUpdateOneOff();
  const remove = useDeleteOneOff();
  const handlers = useSaved(onClose, "One-off event");

  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("");
  const [bucket, setBucket] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [cashDate, setCashDate] = useState("");
  const [amount, setAmount] = useState("");
  const [certainty, setCertainty] = useState("expected");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    setLabel(row?.label ?? "");
    setCategory(row?.category ?? "");
    setBucket(row?.bucket_name ?? "");
    setEffectiveDate(row?.effective_date ?? "");
    setCashDate(row?.cash_date ?? target.date ?? "");
    setAmount(row ? String(row.amount) : "");
    setCertainty(row?.certainty ?? "expected");
    setNotes(row?.notes ?? "");
  }, [row, target.date]);

  const body = {
    label,
    category: category || null,
    bucket_name: bucket || null,
    effective_date: effectiveDate || null,
    cash_date: cashDate,
    amount: Number(amount || 0),
    certainty,
    notes: notes || null,
  };
  const pending = create.isPending || update.isPending || remove.isPending;

  return (
    <>
      <Row label="What is it">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Q3 estimated tax"
        />
      </Row>
      <Row
        label="Amount"
        hint="Negative leaves the bank, positive arrives."
      >
        <Input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="-75000"
        />
      </Row>
      <Row
        label="Cash bucket"
        hint="Without a bucket this shows on the calendar but never reaches the forecast."
      >
        <Select value={bucket} onValueChange={setBucket}>
          <SelectTrigger>
            <SelectValue placeholder="Pick a bucket" />
          </SelectTrigger>
          <SelectContent>
            {bucketNames.map((b) => (
              <SelectItem key={b} value={b}>
                {b}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Row>
      {!bucket && (
        <div className="flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning-light/40 px-3 py-2 text-[11px] text-warning">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
          <span>
            No bucket set — the forecast will list this as ignored rather than
            guess where it belongs.
          </span>
        </div>
      )}
      <Row label="Cash date" hint="When the money actually moves.">
        <Input
          type="date"
          value={cashDate}
          onChange={(e) => setCashDate(e.target.value)}
        />
      </Row>
      <Row label="Confidence">
        <OptionPicker
          label="Certainty"
          value={certainty}
          options={CERTAINTIES}
          onChange={setCertainty}
        />
      </Row>

      {expanded && (
        <>
          <Row
            label="Effective date"
            hint="When the obligation arises — an invoice received, a quarter ending."
          >
            <Input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </Row>
          <Row label="Category" hint="Free-form grouping: Tax, Insurance, Legal…">
            <Input value={category} onChange={(e) => setCategory(e.target.value)} />
          </Row>
          <Row label="Notes">
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Row>
          <p className="rounded-md border px-3 py-2 text-[11px] text-muted-foreground">
            A one-off is an <em>alternative</em> to a manual driver week for the
            same thing, never an addition — entering it both ways double-counts
            it.
          </p>
        </>
      )}

      <Actions
        isNew={row == null}
        pending={pending}
        canSave={!!label && !!cashDate}
        onCancel={onClose}
        onSave={() =>
          row
            ? update.mutate({ id: row.id, body }, handlers)
            : create.mutate(body, handlers)
        }
        onDelete={
          row
            ? () =>
                remove.mutate(row.id, {
                  onSuccess: () => {
                    toast.success("One-off removed");
                    onClose();
                  },
                })
            : undefined
        }
      />
    </>
  );
}

// ── vendor ──────────────────────────────────────────────────────────────────

function VendorForm({
  target,
  onClose,
  expanded,
}: {
  target: Extract<DrawerTarget, { kind: "vendor" }>;
  onClose: () => void;
  expanded: boolean;
}) {
  const row = target.row;
  const create = useCreateVendor();
  const update = useUpdateVendor();
  const remove = useDeleteVendor();
  const handlers = useSaved(onClose, "Vendor payment");

  const [vendor, setVendor] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [cashWeek, setCashWeek] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    setVendor(row?.vendor ?? "");
    setDueDate(row?.due_date ?? target.date ?? "");
    setCashWeek(row?.cash_week ?? target.date ?? "");
    setAmount(row ? String(row.forecast_amount) : "");
    setNotes(row?.notes ?? "");
  }, [row, target.date]);

  const body = {
    vendor,
    due_date: dueDate,
    cash_week: cashWeek,
    forecast_amount: Number(amount || 0),
    notes: notes || null,
  };
  const pending = create.isPending || update.isPending || remove.isPending;

  return (
    <>
      <Row label="Vendor">
        <Input value={vendor} onChange={(e) => setVendor(e.target.value)} />
      </Row>
      <Row label="Amount" hint="Negative leaves the bank.">
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Row>
      <div className="grid grid-cols-2 gap-3">
        <Row label="Due date" hint="When the bill is due.">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Row>
        <Row label="Cash week" hint="The week it actually clears.">
          <Input
            type="date"
            value={cashWeek}
            onChange={(e) => setCashWeek(e.target.value)}
          />
        </Row>
      </div>
      <p className="text-[11px] text-muted-foreground">
        One row per expected payment, not per vendor — amounts and cash weeks
        differ month to month.
      </p>

      {expanded && (
        <Row label="Notes">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Row>
      )}

      <Actions
        isNew={row == null}
        pending={pending}
        canSave={!!vendor && !!cashWeek && !!dueDate}
        onCancel={onClose}
        onSave={() =>
          row
            ? update.mutate({ id: row.id, body }, handlers)
            : create.mutate(body, handlers)
        }
        onDelete={
          row
            ? () =>
                remove.mutate(row.id, {
                  onSuccess: () => {
                    toast.success("Vendor payment removed");
                    onClose();
                  },
                })
            : undefined
        }
      />
    </>
  );
}
